import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { catchError, Observable, tap, throwError } from 'rxjs';
import { environment } from '../../environments/environment';
import {
  ForgotPasswordRequest,
  ForgotPasswordResult,
  PasswordChangeRequest,
  PasswordChangeResponse,
} from '../interfaces/login.interface';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private clientID = environment.client_id;
  private apiUrl = `${environment.int365_dom}/api/v2/Users/Password`;
  private apiUrlSignIn = `${environment.int365_dom}/api/v2/users/signIn`;

  private tokenKey = 'AuthToken';
  private usernameKey = 'firstName'; // Clave para el username
  private lastacessKey = 'lastAcess'; // Clave para el último acceso
  private producerNumberKey = 'producerNumber'; // Clave para el producerNumber
  private producerNameKey = 'producerName'; // Clave para el producerName
  private userIDKey = 'userId'; // Clave para el userId

  // Perfil
  private emailKey = 'email';
  private telefonoCasaKey = 'phone';
  private telefonoCelularKey = 'mobilePhone';
  private temporaryPasswordKey = 'temporaryPassword'; // Añadir esta propiedad con las otras

  constructor(private httpClient: HttpClient, private router: Router) {}

  sigIn(email: string, password: string): Observable<any> {
    const ClientId = this.clientID;
    const body = {
      ClientId, // Asegúrate de que esto sea necesario
      email,
      password,
    };

    return this.httpClient.post<any>(this.apiUrlSignIn, body).pipe(
      tap((response) => {
        if (response.data?.token) {

          this.setToken(response.data.token);
          this.router.navigate(['/inicio']);
          // Perfil
          const email = response.data.email;
          localStorage.setItem(this.emailKey, email);

          const phone = response.data.phoneNumber;
          localStorage.setItem(this.telefonoCasaKey, phone);

          const mobilePhone = response.data.mobileNumber;
          localStorage.setItem(this.telefonoCelularKey, mobilePhone);

          // Producer Number
          const producerNumber = response.data.producerNumber;
          localStorage.setItem(this.producerNumberKey, producerNumber);

          /*Capturar Nombre*/
          const username = response.data.firstName;
          localStorage.setItem(this.usernameKey, username); // Almacena el username en localStorage

          /*Capturar userId */
          const userId = response.data.userId;
          localStorage.setItem(this.userIDKey, userId); // Almacena el userId en localStorage

          /*Capturar userExternalId */
          const userExternalId = response.data.externalId;
          sessionStorage.setItem('userExternalId', userExternalId); // Almacena el userExternalId en localStorage

          /*Capturar ultimo acesso */
          const lastAccess = new Date(response.data.lastAccess); // Convertir a objeto Date

          const formattedLastAccess = this.formatDate(lastAccess); // Formatea la fecha

          localStorage.setItem(this.lastacessKey, formattedLastAccess); // Almacena el último acceso en localStorage

          // Guardar temporaryPassword del response
          const isTemporary = response.data.temporaryPassword;
          this.setTempPassword(isTemporary);
        } else {
          throw new Error('No token returned');
        }
      }),
      catchError((error) => {
        console.error('Login error:', error);
        return throwError(() => ({ error: error.error }));
      })
    );
  }

  // Contrasena olvidada
  forgotPassword(
    request: ForgotPasswordRequest
  ): Observable<ForgotPasswordResult> {
    return this.httpClient
      .post<ForgotPasswordResult>(this.apiUrl, request)
      .pipe(
        tap((response) => response),
        catchError((error) => {
          console.error('Server error:', error); // Mejorar el log de errores

          return throwError(() => error);
        })
      );
  }

  // Guardar datos del perfil
  saveDataPerfil() {
    const email = localStorage.getItem(this.emailKey);
    const telefonoCasa = localStorage.getItem(this.telefonoCasaKey);
    const telefonoCelular = localStorage.getItem(this.telefonoCelularKey);

    return [email, telefonoCasa, telefonoCelular];
  }

  // Cambiar contrasena
  changePassword(
    request: PasswordChangeRequest
  ): Observable<PasswordChangeResponse> {
    const token = this.getToken();
    const headers = {
      Authorization: `Bearer ${token}`,
    };

    return this.httpClient
      .put<PasswordChangeResponse>(this.apiUrl, request, { headers })
      .pipe(
        tap((response) => response),
        catchError((error) => {
          console.error('Server error:', error);
          return throwError(() => error);
        })
      );
  }

  // Función para formatear la fecha
  private formatDate(date: Date): string {
    const optionsDate: Intl.DateTimeFormatOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    };

    const optionsTime: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
    };

    // Obtener el nombre del mes, día y año
    const month = date.toLocaleString('es-ES', { month: 'long' });
    const day = date.toLocaleString('es-ES', { day: 'numeric' });
    const year = date.toLocaleString('es-ES', { year: 'numeric' });

    // Formatear la hora
    const formattedTime = date.toLocaleString('es-ES', optionsTime);

    // Capitalizar la primera letra del mes
    const capitalizedMonth = month.charAt(0).toUpperCase() + month.slice(1);

    // Construir la fecha en el formato deseado: "mes, día, año - hora"
    const finalDate = `${capitalizedMonth} ${day}, ${year} - ${formattedTime}`;

    // Agregar el prefijo "Último acceso: "
    return finalDate;
  }

  private setToken(token: string): void {
    sessionStorage.setItem(this.tokenKey, token);
  }

  getToken(): string | null {
    return sessionStorage.getItem(this.tokenKey);
  }

  /*Capturar nombre */
  getUsername(): string | null {
    // Método para obtener el username
    return localStorage.getItem(this.usernameKey);
  }

  /*Capturar nombre de intermediario */
  getProducerName(): string | null {
    // Método para obtener el username
    return localStorage.getItem(this.producerNameKey);
  }

  /*Capturar utlimo acceso */
  getLastacess(): string | null {
    // Método para obtener el ultimo acceso
    if (localStorage.getItem(this.lastacessKey) === null) {
      return 'No disponible';
    }
    return localStorage.getItem(this.lastacessKey);
  }

  /*Capturar producerNumber */
  getProducerNumber(): number | null {
    const producerNumber = localStorage.getItem(this.producerNumberKey);
    return producerNumber ? +producerNumber : null;
  }

  /*Capturar userId */
  getUserId(): string {
    return localStorage.getItem(this.userIDKey) || '';
  }

  getEmail(): string {
    return localStorage.getItem(this.emailKey) || '';
  }

  // Verificar si el token es válido
  isAuthenticated(): boolean {
    const token = this.getToken();
    if (!token) {
      return false;
    }

    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = payload.exp * 1000;

    return Date.now() < exp;
  }
  /*Cerrar sesion */

  signOut(): void {
    sessionStorage.clear();
    localStorage.removeItem(this.usernameKey); // Elimina el username al cerrar sesión
    localStorage.removeItem(this.lastacessKey); // Elimina el username al cerrar sesión
    localStorage.removeItem(this.temporaryPasswordKey); // Añadir esta línea
    this.router.navigate(['/iniciar-sesion']);
  }

  // Nuevo método para obtener las credenciales del usuario
  getUserCredentials() {
    return {
      producerNumber: this.getProducerNumber(),
      token: this.getToken(),
    };
  }

  // Añadir estos dos métodos
  setTempPassword(isTemp: boolean | null): void {
    if (isTemp !== null) {
      localStorage.setItem(this.temporaryPasswordKey, isTemp.toString());
    }
  }

  getTempPassword(): boolean {
    return localStorage.getItem(this.temporaryPasswordKey) === 'true';
  }
}
